/**
 * @file     filterPills.ts
 * @purpose  Shared dictionary of BC-tailored filter pills used by both the
 *           UI (chip labels) and the API route (predicate expansion).
 *           Two pill kinds:
 *             - `nativeStyle`: maps to a value in mls_properties.architectural_style
 *             - `feature`: maps to keys in mls_properties.{pool|interior|exterior|
 *               community|view|spa|laundry|security|lot|fireplace|cooling|heating}_features
 *               OR direct boolean columns (cooling_yn, fireplace_yn).
 *             - `concept`: NOT a real MLS field — expands to a keyword OR list
 *               applied to public_remarks via ILIKE. Used for buyer-intent
 *               concepts BC agents don't directly tag (Vastu, multi-gen, etc.).
 *
 *           Editing this file is the only place to add a pill. Both the
 *           filter panel and the route consume `PILLS` directly so they
 *           cannot drift.
 */

export type PillKind = 'nativeStyle' | 'feature' | 'concept' | 'orientation' | 'market' | 'geo' | 'attr' | 'tenure';

/** Attribute-band pills (enum/range) resolve against numeric/jsonb columns on
 *  mls_properties directly. */
export type AttrFeatureKey = 'low_strata';

/** Market-signal pills resolve against mls_market_signals (daily-refreshed),
 *  not the per-row mls_property_filters. */
export type MarketFeatureKey = 'new_listing' | 'price_reduced';

/** Geospatial pills resolve against property_access_scores (walk/bike scores
 *  computed from OSM networks + POI cache). */
export type GeoFeatureKey = 'walkable' | 'bikeable' | 'near_transit' | 'good_schools' | 'near_water';

/** Title-type pills resolve against mls_properties.ownership (free text, e.g.
 *  "Freehold NonStrata" | "Leasehold prepaid-NonStrata" | "Co-operative"), via
 *  ILIKE bucketing. 'other' catches co-op/undivided-interest/anything that
 *  isn't clearly freehold or leasehold. */
export type TenureFeatureKey = 'freehold' | 'leasehold' | 'other_tenure';

export interface Pill {
  /** Stable id sent over the wire (URL + analytics). Keep kebab-case. */
  id: string;
  /** User-facing label. */
  label: string;
  kind: PillKind;
  /** Group rendered in the panel. */
  group: 'style' | 'must-have';
  /** For nativeStyle: values to match in architectural_style text[]. */
  styleValues?: string[];
  /** For concept: keywords ORed into public_remarks ILIKE. */
  keywords?: string[];
  /** For feature: pre-built predicate hint consumed by the service. */
  feature?: FeatureKey;
  /** For orientation: compass directions to match (N/NE/E/SE/S/SW/W/NW). */
  compass?: string[];
  /** For market: which mls_market_signals flag to require. */
  market?: MarketFeatureKey;
  /** For geo: which property_access_scores threshold to require. */
  geo?: GeoFeatureKey;
  /** For attr: a numeric/enum band on mls_properties. */
  attr?: AttrFeatureKey;
  /** For tenure: which title-type bucket to require (see TenureFeatureKey). */
  tenure?: TenureFeatureKey;
}

export type FeatureKey =
  | 'garage'
  | 'air_conditioning'
  | 'fireplace'
  | 'pool'
  | 'hot_tub'
  | 'waterfront'
  | 'view'
  | 'suite'
  | 'open_concept'
  | 'multi_gen'
  | 'basement_finished'
  | 'corner_unit'
  | 'renovated'
  | 'vow_data';

export const PILLS: Pill[] = [
  // ── Architectural style (real MLS values) ────────────────────────────────
  { id: 'rancher',     label: 'Rancher / Bungalow', kind: 'nativeStyle', group: 'style',
    styleValues: ['Rancher/Bungalow', 'Rancher/Bungalow w/Bsmt.'] },
  { id: 'character',   label: 'Character / Heritage', kind: 'nativeStyle', group: 'style',
    styleValues: ['Character'] },
  { id: 'penthouse',   label: 'Penthouse', kind: 'nativeStyle', group: 'style',
    styleValues: ['Penthouse'] },
  { id: 'cabin',       label: 'Cabin / Cottage', kind: 'nativeStyle', group: 'style',
    styleValues: ['Cabin', 'Cottage', 'Log house/cabin', 'A-Frame'] },
  { id: 'tudor',       label: 'Tudor / Cape Cod', kind: 'nativeStyle', group: 'style',
    styleValues: ['Tudor', 'Cape Cod'] },
  { id: 'carriage',    label: 'Carriage / Coach', kind: 'nativeStyle', group: 'style',
    styleValues: ['Carriage', 'Carriage/Coach House'] },
  { id: 'split',       label: 'Split-level', kind: 'nativeStyle', group: 'style',
    styleValues: ['Split level entry', 'Split Entry', '3 Level Split', '4 Level Split'] },

  // ── Concept pills (BC buyer-intent, not MLS-tagged) ──────────────────────
  // Concept-pill synonym lists are intentionally short. Each extra synonym
  // adds an OR branch the Postgres planner can't fully short-circuit, so
  // 4 high-signal terms beat 10 low-signal ones for both correctness AND
  // latency. Curate; don't kitchen-sink.
  // East-facing: derived in-DB from listing coords + nearest OSM road
  // segment, stored in listing_orientation. Includes NE+SE for buyers who
  // care about morning sun (broader catchment than strict East).
  { id: 'east-facing', label: 'East-facing', kind: 'orientation', group: 'style',
    compass: ['E', 'NE', 'SE'] },
  // South-facing: afternoon sun / warmth — high buyer intent. Reuses the
  // already-derived listing_orientation data (no DB change).
  { id: 'south-facing', label: 'South-facing', kind: 'orientation', group: 'style',
    compass: ['S', 'SE', 'SW'] },
  { id: 'feng-shui',   label: 'Feng Shui', kind: 'concept', group: 'style',
    keywords: ['feng shui', 'south-facing', 'south facing', 'south exposure'] },
  // Multi-gen + Open concept were remarks-only ILIKE concepts. They now map
  // to canonical is_multi_gen / is_open_concept columns on mls_property_filters
  // (structured tag OR remarks regex, computed once at ingest), so the UI
  // can show a "mentioned in remarks" badge for remarks-only matches.
  { id: 'multi-gen',   label: 'Multi-generational', kind: 'feature', group: 'style', feature: 'multi_gen' },
  { id: 'open-concept',label: 'Open concept', kind: 'feature', group: 'style', feature: 'open_concept' },

  // ── Must-haves (jsonb/text[] feature filters) ────────────────────────────
  { id: 'garage',      label: 'Garage', kind: 'feature', group: 'must-have', feature: 'garage' },
  { id: 'ac',          label: 'Air conditioning', kind: 'feature', group: 'must-have', feature: 'air_conditioning' },
  { id: 'fireplace',   label: 'Fireplace', kind: 'feature', group: 'must-have', feature: 'fireplace' },
  { id: 'pool',        label: 'Pool', kind: 'feature', group: 'must-have', feature: 'pool' },
  { id: 'hot-tub',     label: 'Hot tub / Spa', kind: 'feature', group: 'must-have', feature: 'hot_tub' },
  { id: 'waterfront',  label: 'Waterfront', kind: 'feature', group: 'must-have', feature: 'waterfront' },
  { id: 'view',        label: 'View', kind: 'feature', group: 'must-have', feature: 'view' },
  { id: 'suite',       label: 'Mortgage helper / Suite', kind: 'feature', group: 'must-have', feature: 'suite' },
  { id: 'basement-finished', label: 'Finished basement', kind: 'feature', group: 'must-have', feature: 'basement_finished' },
  { id: 'corner-unit', label: 'Corner unit / lot', kind: 'feature', group: 'must-have', feature: 'corner_unit' },
  { id: 'renovated', label: 'Renovated / updated', kind: 'feature', group: 'must-have', feature: 'renovated' },
  // Membership facet: listings that carry the richer VOW dataset (room-by-room
  // dimensions, finished-area split, price/listing history) for signed-in users.
  { id: 'vow-data', label: 'Full data available', kind: 'feature', group: 'must-have', feature: 'vow_data' },

  // ── Market-signal pills (mls_market_signals, daily-refreshed) ─────────────
  { id: 'new-listing', label: 'New this week', kind: 'market', group: 'must-have', market: 'new_listing' },
  { id: 'price-reduced', label: 'Price reduced', kind: 'market', group: 'must-have', market: 'price_reduced' },

  // ── Geospatial pills (property_access_scores, OSM-derived) ────────────────
  { id: 'walkable', label: 'Very walkable', kind: 'geo', group: 'must-have', geo: 'walkable' },
  { id: 'bikeable', label: 'Bike-friendly', kind: 'geo', group: 'must-have', geo: 'bikeable' },
  { id: 'near-transit', label: 'Near rapid transit', kind: 'geo', group: 'must-have', geo: 'near_transit' },
  { id: 'good-schools', label: 'Near a top school', kind: 'geo', group: 'must-have', geo: 'good_schools' },
  { id: 'near-water', label: 'By the water', kind: 'geo', group: 'must-have', geo: 'near_water' },

  // ── Attribute-band pills (enum/range on mls_properties) ───────────────────
  { id: 'low-strata-fee', label: 'Low strata fee', kind: 'attr', group: 'must-have', attr: 'low_strata' },

  // ── Title-type pills (mls_properties.ownership, ILIKE-bucketed) ───────────
  { id: 'freehold', label: 'Freehold', kind: 'tenure', group: 'must-have', tenure: 'freehold' },
  { id: 'leasehold', label: 'Leasehold', kind: 'tenure', group: 'must-have', tenure: 'leasehold' },
  { id: 'other-tenure', label: 'Co-op / other', kind: 'tenure', group: 'must-have', tenure: 'other_tenure' },
];

export const PILLS_BY_ID: Record<string, Pill> = Object.fromEntries(PILLS.map((p) => [p.id, p]));

export interface PillPredicates {
  styleValues: string[];
  features: string[];
  marketFeatures: string[];
  geoFeatures: string[];
  attrFeatures: string[];
  tenureValues: string[];
  orientationCompass: string[];
  /** websearch OR groups for concept pills, ANDed into the full-text query. */
  conceptQueries: string[];
}

/** The search predicates a set of pill ids selects. Unknown ids are dropped. */
export function expandPills(ids: readonly string[]): PillPredicates {
  const out: PillPredicates = {
    styleValues: [], features: [], marketFeatures: [], geoFeatures: [],
    attrFeatures: [], tenureValues: [], orientationCompass: [], conceptQueries: [],
  };
  for (const id of new Set(ids)) {
    const pill = PILLS_BY_ID[id];
    if (!pill) continue;
    if (pill.kind === 'nativeStyle' && pill.styleValues) out.styleValues.push(...pill.styleValues);
    if (pill.kind === 'feature' && pill.feature) out.features.push(pill.feature);
    if (pill.kind === 'market' && pill.market) out.marketFeatures.push(pill.market);
    if (pill.kind === 'geo' && pill.geo) out.geoFeatures.push(pill.geo);
    if (pill.kind === 'attr' && pill.attr) out.attrFeatures.push(pill.attr);
    if (pill.kind === 'tenure' && pill.tenure) out.tenureValues.push(pill.tenure);
    if (pill.kind === 'orientation' && pill.compass) out.orientationCompass.push(...pill.compass);
    if (pill.kind === 'concept' && pill.keywords && pill.keywords.length > 0) {
      out.conceptQueries.push(`(${pill.keywords.map((kw) => (kw.includes(' ') ? `"${kw}"` : kw)).join(' OR ')})`);
    }
  }
  const uniq = (v: string[]) => Array.from(new Set(v));
  return {
    ...out,
    marketFeatures: uniq(out.marketFeatures),
    geoFeatures: uniq(out.geoFeatures),
    attrFeatures: uniq(out.attrFeatures),
    tenureValues: uniq(out.tenureValues),
    orientationCompass: uniq(out.orientationCompass),
  };
}

/** Pills exposed in the "Architectural style" chip row. */
export const STYLE_PILLS = PILLS.filter((p) => p.group === 'style');
/** Pills exposed in the "Must-haves" chip row. Excludes tenure pills, which
 *  get their own labeled "Title type" row (see TENURE_PILLS) rather than
 *  being buried in the must-have grab-bag. */
export const MUST_HAVE_PILLS = PILLS.filter((p) => p.group === 'must-have' && p.kind !== 'tenure');

/** Pills exposed in the "Title type" chip row. */
export const TENURE_PILLS = PILLS.filter((p) => p.kind === 'tenure');

/** Geo pills whose ground-truth data is Metro-Vancouver-only. near-transit
 *  (SkyTrain) does not exist elsewhere in BC; the near-water coastline set and
 *  the top-school catchments were only built for the Metro footprint, so these
 *  return zero everywhere else and must be hidden outside it. walkable/bikeable
 *  are NOT here: walk/bike scores are computed province-wide (both feeds), so
 *  those pills work anywhere. */
export const METRO_ONLY_PILL_IDS: ReadonlySet<string> = new Set([
  'near-transit',
  'good-schools',
  'near-water',
]);

/** Presentation clusters for the Features panel. `group` is the legacy two-row
 *  split (style / must-have); these are the finer buckets a buyer actually
 *  thinks in, derived from `kind` so they cannot drift from the predicates. */
export type PillCluster = 'home' | 'style' | 'light' | 'area' | 'listing';

export const PILL_CLUSTER_ORDER: PillCluster[] = ['home', 'style', 'light', 'area', 'listing'];

export const PILL_CLUSTER_LABEL: Record<PillCluster, string> = {
  home: 'The home',
  style: 'Style',
  light: 'Light & orientation',
  area: 'The neighbourhood',
  listing: 'Listing & timing',
};

// Tenure pills live in the "Home & location" section (LocationSection /
// TraditionalSearchPanel's property-type row) alongside property type, not
// in the Features chip clusters — title type is a property fact, not a
// lifestyle feature. FeaturesSection filters PILLS to exclude kind 'tenure'
// so they don't render twice.
export function pillCluster(pill: Pill): PillCluster {
  switch (pill.kind) {
    case 'nativeStyle':
      return 'style';
    case 'orientation':
    case 'concept':
      return 'light';
    case 'geo':
      return 'area';
    case 'market':
      return 'listing';
    default:
      // multi-gen / open-concept are layout ideas, not fixtures.
      if (pill.feature === 'multi_gen' || pill.feature === 'open_concept') return 'style';
      if (pill.feature === 'vow_data') return 'listing';
      return 'home';
  }
}

/** Pills whose match is inferred from listing remarks or derived geometry
 *  rather than a structured MLS field. Surfaced in the UI as a provenance
 *  marker so the caveat rides the pill instead of a block of helper text. */
export function isInferredPill(pill: Pill): boolean {
  return (
    pill.kind === 'concept' ||
    pill.kind === 'orientation' ||
    pill.feature === 'multi_gen' ||
    pill.feature === 'open_concept'
  );
}
